import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  HeadphonesIcon,
  AlertTriangle,
  ScrollText,
  Mail,
  MessageSquare,
  ShieldCheck,
  ImageIcon,
  Gift,
  Link2,
  Share2,
  UserPlus,
  Layers,
  Rocket,
  TrendingUp,
  Target,
  Shield,
  Activity,
  BellRing,
  Heart,
  Settings,
  ListChecks,
  Sparkles,
  Inbox,
  ChevronDown,
  Clapperboard,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAdminPermissions } from '@/contexts/AdminPermissionsContext';

/**
 * Grouped navigation — items are organized into operational domains
 * (Overview / Growth / Members / Matching / Trust & Safety / Product /
 * Platform) instead of a flat list. Groups are collapsible and the
 * collapsed set is persisted to localStorage so an operator's layout
 * sticks across sessions.
 *
 * Each item is permission-gated via the optional `perm` field; items with
 * `perm: undefined` show for every authenticated admin. A group with zero
 * visible items is hidden entirely. Permission keys MUST match the seed
 * catalog (migrations 210/212). Adding a section: pick the matching
 * `*.list` / `*.read` key and drop it in the right group.
 */
type NavItem = {
  title: string;
  href: string;
  icon: typeof LayoutDashboard;
  perm?: string;
};

type NavGroup = {
  group: string;
  items: NavItem[];
};

const navGroups: NavGroup[] = [
  {
    group: 'Overview',
    items: [
      { title: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, perm: 'analytics.dashboard.read' },
      { title: 'Launch Command Center', href: '/launch-command-center', icon: Rocket, perm: 'analytics.launch_cc.read' },
      // Analytics Agent (2026-08-21). Gated on `agent.chat`, the TRANSPORT key
      // migration 311 grants to every bundle that already holds
      // `analytics.dashboard.read` — the same audience, so this appears for
      // exactly the people who already read the dashboard it cites.
      //
      // 🔴 REGISTERED HERE AND NOWHERE ELSE. `App.tsx`'s 16-entry `resources`
      // array is rendered by NOTHING: `useMenu` is the only Refine hook that
      // builds a menu from it and has zero occurrences in this tree. Adding a
      // `resources` entry would be dead config implying navigation it does not
      // provide, and the agent page uses neither `useList` nor the
      // data-provider resource map.
      { title: 'Analytics Agent', href: '/agent', icon: Sparkles, perm: 'agent.chat' },
    ],
  },
  {
    group: 'Growth',
    items: [
      { title: 'Acquisition', href: '/acquisition', icon: Target, perm: 'analytics.acquisition.read' },
      { title: 'Growth', href: '/growth', icon: TrendingUp, perm: 'analytics.growth.read' },
      { title: 'Waitlist', href: '/waitlist', icon: Mail, perm: 'growth.waitlist.read' },
      { title: 'Beta Applications', href: '/beta-applications', icon: Rocket, perm: 'growth.waitlist.read' },
      { title: 'Promotions', href: '/promotions', icon: Gift, perm: 'growth.promotions.read' },
      // Above Referrals on purpose: the builder is where a tagged link should be
      // made, and it must be found BEFORE the referral minting flow. Shares
      // `growth.referrals.read` — same audience, and a dedicated key would cost
      // a migration on a stack near its CFN-output cap.
      { title: 'Campaign Links', href: '/campaign-links', icon: Link2, perm: 'growth.referrals.read' },
      { title: 'Referrals', href: '/referrals', icon: Share2, perm: 'growth.referrals.read' },
      { title: 'Bulk Grant Coupons', href: '/coupons/bulk-grant', icon: UserPlus, perm: 'growth.coupons.bulk_grant' },
      { title: 'BETA Cohorts', href: '/segments', icon: Layers, perm: 'growth.segments.bulk_grant' },
    ],
  },
  {
    group: 'Members',
    items: [
      { title: 'Users', href: '/users', icon: Users, perm: 'users.list' },
      { title: 'Support', href: '/support', icon: HeadphonesIcon, perm: 'users.lookup' },
      // Support inbox (design 2026-09-26, section 6). `support_inbox.read` is
      // seeded by migration 349 for super_admin and support.
      { title: 'Support Inbox', href: '/support-inbox', icon: Inbox, perm: 'support_inbox.read' },
    ],
  },
  {
    group: 'Matching',
    items: [
      { title: 'Matches', href: '/matches', icon: Heart, perm: 'matches.list' },
    ],
  },
  {
    group: 'Trust & Safety',
    items: [
      { title: 'Verification', href: '/verification', icon: ShieldCheck, perm: 'moderation.verification.review' },
      { title: 'Verification Whitelist', href: '/verification/whitelist', icon: ShieldCheck, perm: 'users.verification_whitelist' },
      { title: 'Photo Moderation', href: '/moderation', icon: ImageIcon, perm: 'moderation.photos.review' },
      { title: 'Reports', href: '/reports', icon: AlertTriangle, perm: 'moderation.reports.review' },
    ],
  },
  {
    group: 'Product',
    items: [
      // No `perm` → shows for every authenticated admin (no seeded permission
      // key for the question catalog; route is likewise ungated, matching the
      // promotions pattern). The backend reads are admin-authed via verifyAdminAuth.
      { title: 'Onboarding Questions', href: '/questions', icon: ListChecks },
      { title: 'Feedback', href: '/feedback', icon: MessageSquare, perm: 'feedback.read' },
    ],
  },
  {
    group: 'Platform',
    items: [
      { title: 'System Health', href: '/system-health', icon: Activity, perm: 'analytics.system_health.read' },
      // Notification rails v2 (2026-09-22). `notifications.read` is seeded by
      // migration 343 for super_admin, growth and marketing. Registered HERE,
      // like every other entry: App.tsx's `resources` array renders nothing.
      { title: 'Notifications', href: '/notifications', icon: BellRing, perm: 'notifications.read' },
      { title: 'Audit Log', href: '/audit', icon: ScrollText, perm: 'audit.read_own' },
      { title: 'Security', href: '/security', icon: Shield, perm: 'admin.list' },
      // App Experience (spec 2026-10-23 §5): the intro video and the Abby
      // welcome. `experience.read` is seeded by migration 388.
      { title: 'App Experience', href: '/app-experience', icon: Clapperboard, perm: 'experience.read' },
      { title: 'Settings', href: '/settings', icon: Settings, perm: 'settings.read' },
    ],
  },
];

const COLLAPSE_KEY = 'admin-nav-collapsed-groups';

function loadCollapsed(): Set<string> {
  try {
    const raw = localStorage.getItem(COLLAPSE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? new Set(arr as string[]) : new Set();
  } catch {
    return new Set();
  }
}

export function Sidebar() {
  const { hasPermission, loaded } = useAdminPermissions();
  const [collapsed, setCollapsed] = useState<Set<string>>(loadCollapsed);

  const toggleGroup = (group: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      try {
        localStorage.setItem(COLLAPSE_KEY, JSON.stringify([...next]));
      } catch {
        /* localStorage unavailable (private mode) — collapse is non-essential */
      }
      return next;
    });
  };

  // While the first /v1/admin/me fetch is in flight, render no items so we
  // don't flash an empty sidebar that immediately repopulates. Filter at the
  // item level, then drop any group left with zero visible items.
  const visibleGroups = loaded
    ? navGroups
        .map((g) => ({ ...g, items: g.items.filter((i) => !i.perm || hasPermission(i.perm)) }))
        .filter((g) => g.items.length > 0)
    : [];

  return (
    <aside className="flex h-screen w-64 flex-col border-r bg-card">
      <div className="flex h-16 shrink-0 items-center border-b px-6">
        <h1 className="text-xl font-bold">UDC Admin</h1>
      </div>
      <nav className="flex-1 space-y-4 overflow-y-auto p-4">
        {visibleGroups.map((group) => {
          const isCollapsed = collapsed.has(group.group);
          return (
            <div key={group.group}>
              <button
                type="button"
                onClick={() => toggleGroup(group.group)}
                aria-expanded={!isCollapsed}
                className="flex w-full items-center justify-between px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70 transition-colors hover:text-foreground"
              >
                <span>{group.group}</span>
                <ChevronDown
                  className={cn('h-3 w-3 transition-transform', isCollapsed && '-rotate-90')}
                  aria-hidden
                />
              </button>
              {!isCollapsed && (
                <div className="mt-1 space-y-1">
                  {group.items.map((item) => (
                    <NavLink
                      key={item.href}
                      to={item.href}
                      // `end` matches the path EXACTLY, not as a prefix, so
                      // `/verification` doesn't also highlight while on
                      // `/verification/whitelist`.
                      end
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                          isActive
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                        )
                      }
                    >
                      <item.icon className="h-4 w-4" />
                      {item.title}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
